import { useState, useRef, useEffect } from 'react';
import { Moon, Sun, LogOut, ChevronUp, Globe, Map } from 'lucide-react';

export interface GsUserMenuUser {
  name: string;
  email?: string;
  role: string;
  initials: string;
  photoUrl?: string;
}

export interface GsUserMenuProps {
  user: GsUserMenuUser;
  isDark?: boolean;
  onToggleDark?: () => void;
  onLogout?: () => void;
  /** Tour guidé / onboarding — affiche « Revoir le tour » si défini */
  onStartTour?: () => void;
  variant?: 'navy' | 'light';
  appName?: string;
  version?: string;
  /** Quand la sidebar est rétractée — affiche uniquement l'avatar initiales */
  collapsed?: boolean;
}

export function GsUserMenu({
  user, isDark = false, onToggleDark, onLogout, onStartTour,
  variant = 'light', appName, version, collapsed = false,
}: GsUserMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = `gs-user-menu-${user.initials}`;
  const isLight = variant === 'light';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const footBorder = isLight
    ? 'border-t border-[#e2e8f0] dark:border-slate-800'
    : 'border-t-[0.5px] border-t-[rgba(255,255,255,0.06)]';
  const footName = isLight ? 'text-slate-900 dark:text-slate-100' : 'text-white';
  const footRole = isLight ? 'text-slate-500 dark:text-slate-400' : 'text-[#334155]';
  const menuBg = isLight
    ? 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700'
    : 'bg-[#1e293b] border border-white/10';
  const menuText = isLight ? 'text-slate-700 dark:text-slate-200' : 'text-slate-200';
  const menuHover = isLight ? 'hover:bg-slate-100 dark:hover:bg-slate-800' : 'hover:bg-white/10';
  const menuDivider = isLight
    ? 'border-t border-slate-100 dark:border-slate-800'
    : 'border-t border-white/10';
  const menuMuted = isLight ? 'text-slate-500 dark:text-slate-400' : 'text-slate-400';

  return (
    <div ref={containerRef} className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(p => !p)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Menu utilisateur — ${user.name}`}
        title={collapsed ? user.name : undefined}
        className={[
          'flex w-full items-center transition-colors hover:bg-black/5 dark:hover:bg-white/5 touch-manipulation',
          footBorder,
          collapsed ? 'justify-center px-0 py-3' : 'gap-2.5 px-3 py-3',
        ].join(' ')}
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-500 text-[10px] font-semibold text-white">
          {user.initials}
        </div>
        {!collapsed && (
          <>
            <div className="min-w-0 flex-1 text-left">
              <div className={`truncate text-[11px] font-medium ${footName}`}>{user.name}</div>
              <div className={`truncate text-[9px] ${footRole}`}>{user.role}</div>
            </div>
            <ChevronUp size={13} className={`shrink-0 transition-transform ${footRole} ${open ? 'rotate-0' : 'rotate-180'}`} aria-hidden />
          </>
        )}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Options utilisateur"
          className={[
            'absolute z-50 overflow-hidden rounded-xl shadow-xl',
            collapsed
              ? 'left-full bottom-0 right-auto ml-2 mb-0 w-52'
              : 'bottom-full left-0 right-0 mb-1',
            menuBg,
          ].join(' ')}
        >
          <div className="px-3 py-2.5">
            <div className={`text-[11px] font-semibold ${footName}`}>{user.name}</div>
            <div className={`mt-0.5 text-[9px] ${footRole}`}>{user.role}</div>
            {user.email && <div className={`mt-1 text-[10px] ${menuMuted}`}>{user.email}</div>}
          </div>

          <div className={menuDivider} />

          {onToggleDark && (
            <button type="button" role="menuitem" onClick={onToggleDark}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-[12px] ${menuText} ${menuHover} transition-colors touch-manipulation`}>
              <span className="flex items-center gap-2">
                {isDark ? <Sun size={14} className="text-amber-400" /> : <Moon size={14} />}
                {isDark ? 'Mode clair' : 'Mode sombre'}
              </span>
              <span className={`relative h-4 w-7 rounded-full transition-colors ${isDark ? 'bg-brand-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${isDark ? 'translate-x-[14px]' : 'translate-x-0.5'}`} />
              </span>
            </button>
          )}

          <div className={menuDivider} />

          <div role="menuitem" tabIndex={-1}
            className={`flex w-full cursor-default items-center justify-between px-3 py-2 text-[12px] ${menuText} ${menuMuted}`}>
            <span className="flex items-center gap-2"><Globe size={14} />Langue</span>
            <span className="font-medium">FR</span>
          </div>

          {onStartTour && (
            <>
              <div className={menuDivider} />
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onStartTour();
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[12px] text-slate-500 transition-colors hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800 touch-manipulation"
              >
                <Map size={14} className="shrink-0" />
                Revoir le tour
              </button>
            </>
          )}

          {onLogout && (
            <>
              <div className={menuDivider} />
              <button type="button" role="menuitem"
                onClick={() => { setOpen(false); onLogout(); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30 transition-colors touch-manipulation">
                <LogOut size={14} />
                Se déconnecter
              </button>
            </>
          )}

          {(appName || version) && (
            <>
              <div className={menuDivider} />
              <div className={`px-3 py-2 text-[9px] ${menuMuted}`}>
                {[appName, version].filter(Boolean).join(' · ')}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
